import { useEffect, useState, useMemo } from 'react';
import * as apiGetUser from 'api/user/GetUserIdV1';
import { useJwt } from 'hooks/useJwt';

type UserData = apiGetUser.ResponseData | null;

export function useUser() {
	const { accessJwt } = useJwt();
	const [user, setUser] = useState<UserData>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<Error | null>(null);

	useEffect(() => {
		const userId = accessJwt?.nameid;
		
		if (!userId) {
			setUser(null);
			setLoading(false);
			return;
		}

		const fetchUser = async () => {
			setLoading(true);
			setError(null);
			
			try {
				const result = await apiGetUser.callApi(userId);
				const userData = result.toNullable();
				setUser(userData);
			} catch (err) {
				const error = err instanceof Error ? err : new Error('Failed to fetch user data');
				setError(error);
				setUser(null);
			} finally {
				setLoading(false);
			}
		};

		fetchUser();
	}, [accessJwt?.nameid]);

	const isSystemDeveloper = useMemo(() => {
		if (!user?.roles || !Array.isArray(user.roles)) return false;
		
		// Handle both string array and object array formats
		return user.roles.some((role) => {
			// If roles is an array of strings
			if (typeof role === 'string') {
				return role === 'Develop' || role === "Food Report Validator";
			}
			// If roles is an array of objects (for type safety)
			if (typeof role === 'object' && role !== null) {
				// Check if role name is "Develop" or "Developer"
				if (role.name === 'Develop' || role.name === "Food Report Validator") {
					return true;
				}
				// Also check if role has "Develop" permission
				if (role.permissions && Array.isArray(role.permissions)) {
					return role.permissions.some((permission) => permission.systemName === "Food Report Validator");
				}
			}
			return false;
		});
	}, [user?.roles]);

	return {
		user,
		roles: user?.roles ?? [],
		loading,
		error,
		userId: accessJwt?.nameid ?? null,
		isSystemDeveloper,
	};
}